import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import Checkbox from 'components/checkbox';
import Nodatafound from 'pages/Common/Nodatafound';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { ThreeDots } from 'react-loader-spinner';
import toast from 'react-hot-toast';
import { MdArrowBack } from 'react-icons/md';

import companyServices from 'ApiService/Placement/companyServices';
import clientServices from 'ApiService/Placement/clientServices';
import departmentService from 'ApiService/SuperAdmin/departmentService';
import jobRoleServices from 'ApiService/Placement/jobRoleServices';
import studentAssignmentServices from 'ApiService/Placement/studentAssignmentServices';
import { AuthContext } from 'contexts/AuthContext';

const MAX_JD_SIZE = 10 * 1024 * 1024;
const JD_ACCEPT = '.pdf,.doc,.docx,.txt,.rtf,.odt,.png,.jpg,.jpeg';

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Index = () => {
  const { state } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const prefill = location.state?.prefill;
  const prefillAppliedRef = useRef(false);

  // --- Configuration State ---
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedRoleName, setSelectedRoleName] = useState(''); // New state for role name
  const [selectedRole, setSelectedRole] = useState('');
  // 'role' = pick a JobRole template, 'attachment' = upload a JD file
  const [jdSource, setJdSource] = useState('role');
  const [jdFile, setJdFile] = useState(null);
  // Position title HR enters when attaching a JD (no JobRole template to name it)
  const [jobTitle, setJobTitle] = useState('');
  const jdFileInputRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [notes, setNotes] = useState('');
  const [assignmentExpiry, setAssignmentExpiry] = useState(24);

  const [clients, setClients] = useState([]);
  const [roles, setRoles] = useState([]);
  const selectedCompanyObj = companies.find((c) => c._id === selectedCompany);

  // Modals
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [newRole, setNewRole] = useState({
    roleName: '',
    description: '',
    skills: '',
    experienceLevel: 'Fresher',
  });
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [newClient, setNewClient] = useState({
    clientName: '',
    location: '',
    contactPerson: '',
    contactEmail: '',
  });
  const [isModalOpenConfirmation, setIsModalOpenConfirmation] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  // --- Student Data/Filter State ---
  const [studentDetails, setStudentDetails] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // Gender
  const [jobPriorityFilter, setJobPriorityFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [salaryFilter, setSalaryFilter] = useState('all');
  const [educationFilter, setEducationFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [percentageRange, setPercentageRange] = useState({ min: '', max: '' });

  const [uniqueLocations, setUniqueLocations] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [uniqueEducationLevels, setUniqueEducationLevels] = useState([]);
  const [uniqueCourses, setUniqueCourses] = useState([]);

  // --- Initialization ---
  useEffect(() => {
    fetchInitialConfigData();
  }, []);

  const fetchInitialConfigData = async () => {
    try {
      const [companiesRes, departmentsRes] = await Promise.all([
        companyServices.getAllCompanies(),
        departmentService.getAllDepartments(),
      ]);
      if (companiesRes.data.success)
        setCompanies(companiesRes.data.companies.filter((c) => c.isActive));
      if (departmentsRes.data.success) setDepartments(departmentsRes.data.data);
    } catch (error) {
      toast.error('Failed to fetch companies or departments');
    }
  };

  /**
   * Arriving from an Ongoing Interviews card ("Assign Student") carries the
   * drive's company / department / role in router state. Apply it once the
   * dropdown sources have loaded, running the same cascades the change
   * handlers do so clients and roles populate as if the user had picked them.
   */
  useEffect(() => {
    if (!prefill || prefillAppliedRef.current) return;
    if (companies.length === 0 || departments.length === 0) return;
    prefillAppliedRef.current = true;

    (async () => {
      if (prefill.companyId) {
        setSelectedCompany(prefill.companyId);
        const company = companies.find((c) => c._id === prefill.companyId);
        if (company?.hasClients) fetchClients(prefill.companyId);
      }

      if (prefill.departmentId) {
        setSelectedDepartment(prefill.departmentId);
        try {
          const response = await jobRoleServices.getRolesByDepartment(
            prefill.departmentId
          );
          const fetchedRoles = response.data.success
            ? response.data.roles
            : [];
          setRoles(fetchedRoles);

          // The role picker is two-stage: role name, then the specific role.
          const role = fetchedRoles.find((r) => r._id === prefill.roleId);
          if (role) {
            setSelectedRoleName(role.roleName);
            setSelectedRole(role._id);
          }
        } catch {
          setRoles([]);
        }
      }
    })();
  }, [prefill, companies, departments]); // eslint-disable-line

  // --- Filter Change Handlers ---
  // eslint-disable-next-line no-unused-vars
  const handlePercentageChange = (type) => (event) => {
    const value = event.target.value;
    if (
      value === '' ||
      (/^\d*\.?\d*$/.test(value) && parseFloat(value) <= 100)
    ) {
      setPercentageRange((prev) => ({ ...prev, [type]: value }));
    }
  };

  // --- Student Data Fetching ---
  const fetchStudents = async () => {
    setLoadingStudents(true);
    try {
      const response =
        await studentAssignmentServices.getStudentsForAssignment();
      if (response.data.success) {
        const priorityOrder = { High: 1, Medium: 2, Low: 3 };
        const studentsWithSelection = response.data.students
          .map((student) => ({
            ...student,
            id: student._id,
            selected: false,
            // Fallbacks in case any schema defaults are missing
            jobLocationPreference: student.jobLocationPreference || [],
            academicRecords: student.academicRecords || [],
            placementPriority: student.placementPriority || 'Low',
            gender: student.gender || 'Unknown',
            email: student.email || '',
            contactNumber: student.contactNumber || '',
          }))
          .sort(
            (a, b) =>
              priorityOrder[a.placementPriority] -
              priorityOrder[b.placementPriority]
          );

        setStudentDetails(studentsWithSelection);

        if (response.data.students.length === 0)
          toast.error('No students found');
      } else {
        setStudentDetails([]);
      }
    } catch (error) {
      setStudentDetails([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [state]); // eslint-disable-line

  // --- Dependent Lookups ---
  useEffect(() => {
    if (studentDetails.length > 0) {
      const locations = new Set();
      const educationLevels = new Set();
      const courses = new Set();
      studentDetails.forEach((student) => {
        student.jobLocationPreference.forEach((l) => locations.add(l));
        student.academicRecords?.forEach((r) => educationLevels.add(r.level));
        student.enrollments
          ?.filter((e) => e.current)
          ?.forEach((e) => {
            // If a department is selected, only show courses belonging to that department
            if (
              !selectedDepartment ||
              e.course?.department === selectedDepartment
            ) {
              if (e.course?.courseName) courses.add(e.course.courseName);
            }
          });
      });
      setUniqueLocations(Array.from(locations));
      setUniqueEducationLevels(Array.from(educationLevels));
      setUniqueCourses(Array.from(courses));
    }
  }, [studentDetails, selectedDepartment]);

  // Reset course filter if the currently selected course is no longer in the uniqueCourses list
  useEffect(() => {
    if (courseFilter !== 'all' && !uniqueCourses.includes(courseFilter)) {
      setCourseFilter('all');
    }
  }, [uniqueCourses, courseFilter]);

  // --- Apply Filters ---
  useEffect(() => {
    let result = [...studentDetails];

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(lower) ||
          s.email.toLowerCase().includes(lower) ||
          s.contactNumber.toLowerCase().includes(lower)
      );
    }
    if (filter !== 'all')
      result = result.filter((s) => s.gender.toLowerCase() === filter);
    if (jobPriorityFilter !== 'all')
      result = result.filter(
        (s) => s.placementPriority.toLowerCase() === jobPriorityFilter
      );
    if (locationFilter !== 'all')
      result = result.filter((s) =>
        s.jobLocationPreference.includes(locationFilter)
      );
    if (salaryFilter !== 'all')
      result = result.filter((s) => s.preferredSalary === salaryFilter);
    if (educationFilter !== 'all')
      result = result.filter((s) =>
        s.academicRecords?.some((r) => r.level === educationFilter)
      );
    if (courseFilter !== 'all')
      result = result.filter((s) =>
        s.enrollments?.some(
          (e) => e.current && e.course?.courseName === courseFilter
        )
      );
    if (percentageRange.min !== '' || percentageRange.max !== '') {
      result = result.filter((s) => {
        return s.academicRecords?.some((r) => {
          const p = r.percentage || 0;
          const minOk =
            percentageRange.min === '' || p >= parseFloat(percentageRange.min);
          const maxOk =
            percentageRange.max === '' || p <= parseFloat(percentageRange.max);
          return minOk && maxOk;
        });
      });
    }

    if (selectedDepartment) {
      result = result.filter((s) =>
        s.enrollments?.some(
          (e) => e.current && e.course?.department === selectedDepartment
        )
      );
    }

    setFilteredData(result);
  }, [
    studentDetails,
    filter,
    jobPriorityFilter,
    locationFilter,
    salaryFilter,
    educationFilter,
    courseFilter,
    percentageRange,
    searchTerm,
    selectedDepartment,
  ]);

  function resetFilters() {
    setFilter('all');
    setSearchTerm('');
    setJobPriorityFilter('all');
    setLocationFilter('all');
    setSalaryFilter('all');
    setEducationFilter('all');
    setCourseFilter('all');
    setPercentageRange({ min: '', max: '' });
  }

  // --- Configuration Handlers ---
  const handleCompanyChange = async (e) => {
    const compId = e.target.value;
    setSelectedCompany(compId);
    setSelectedClient('');
    setClients([]);
    const comp = companies.find((c) => c._id === compId);
    if (comp && comp.hasClients) {
      fetchClients(compId);
    }
  };

  const fetchClients = async (companyId) => {
    try {
      const response = await clientServices.getClientsByCompany(companyId);
      if (response.data.success)
        setClients(response.data.clients.filter((c) => c.isActive));
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  };

  const handleDepartmentChange = async (e) => {
    const deptId = e.target.value;
    setSelectedDepartment(deptId);
    setSelectedRoleName('');
    setSelectedRole('');
    if (deptId) {
      try {
        const response = await jobRoleServices.getRolesByDepartment(deptId);
        if (response.data.success) {
          setRoles(response.data.roles);
        } else {
          setRoles([]);
        }
      } catch (err) {
        setRoles([]);
      }
    } else {
      setRoles([]);
    }
  };

  const handleRoleNameChange = (e) => {
    const roleName = e.target.value;
    setSelectedRoleName(roleName);
    setSelectedRole(''); // Reset specific role ID when name changes
  };

  const handleJdSourceChange = (source) => {
    setJdSource(source);
    // Attaching a JD replaces the role template, so clear the role selection.
    if (source === 'attachment') {
      setSelectedRoleName('');
      setSelectedRole('');
    } else {
      setJobTitle('');
    }
  };

  const clearJdFile = () => {
    setJdFile(null);
    if (jdFileInputRef.current) jdFileInputRef.current.value = '';
  };

  const handleJdFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return clearJdFile();
    if (file.size > MAX_JD_SIZE) {
      toast.error('JD file must be 10 MB or smaller.');
      return clearJdFile();
    }
    setJdFile(file);
  };

  const uniqueRoleNames = Array.from(new Set(roles.map((r) => r.roleName)));
  const availableExperienceLevels = roles.filter(
    (r) => r.roleName === selectedRoleName
  );

  // --- Submission ---
  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setStudentDetails((prev) =>
      prev.map((s) => {
        // Only toggle those currently visible in filter
        if (filteredData.some((fd) => fd._id === s._id)) {
          return { ...s, selected: isChecked };
        }
        return s;
      })
    );
  };

  const handleCheckboxChange = (studentId) => {
    setStudentDetails((prev) =>
      prev.map((student) =>
        student._id === studentId
          ? { ...student, selected: !student.selected }
          : student
      )
    );
  };

  const triggerConfirmation = () => {
  const selectedCount = studentDetails.filter((s) => s.selected).length;
    if (selectedCount === 0)
      return toast.error('Please select at least one student.');
    if (!selectedCompany) return toast.error('Please select a company.');
    if (selectedCompanyObj?.hasClients && !selectedClient)
      return toast.error('Please select a client.');
    if (!selectedDepartment) return toast.error('Please select a department.');
    if (jdSource === 'role' && !selectedRole)
      return toast.error('Please select a role.');
    if (jdSource === 'attachment' && !jdFile)
      return toast.error('Please attach a JD file.');
    if (jdSource === 'attachment' && !jobTitle.trim())
      return toast.error('Please enter a role.');

    setIsModalOpenConfirmation(true);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const selectedStudentIds = studentDetails
        .filter((s) => s.selected)
        .map((s) => s._id);
      const formData = new FormData();
      formData.append('studentIds', JSON.stringify(selectedStudentIds));
      formData.append('companyId', selectedCompany);
      formData.append('clientId', selectedClient || '');
      formData.append('departmentId', selectedDepartment);
      formData.append('jdSource', jdSource);
      if (jdSource === 'role') formData.append('roleId', selectedRole);
      if (jdSource === 'attachment') formData.append('jobTitle', jobTitle.trim());
      formData.append('notes', notes);
      formData.append('assignmentExpiry', String(assignmentExpiry));
      if (jdFile) formData.append('jdFile', jdFile);

      const response = await studentAssignmentServices.assignStudentToCompany(
        formData
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setIsModalOpenConfirmation(false);
        // Clear selection
        setStudentDetails((prev) =>
          prev.map((s) => ({ ...s, selected: false }))
        );
        clearJdFile();
        setJobTitle('');
      } else {
        toast.error(response.data.message || 'Failed to assign students');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to assign students'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Inline Creation Handlers ---
  const handleAddNewRole = async (e) => {
    e.preventDefault();
    if (!selectedDepartment)
      return toast.error('Please select a department first');
    try {
      const roleData = {
        departmentId: selectedDepartment,
        roleName: newRole.roleName,
        description: newRole.description,
        skills: newRole.skills
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        experienceLevel: newRole.experienceLevel || 'Fresher',
      };
      const response = await jobRoleServices.createRole(roleData);
      if (response.data.success) {
        toast.success('Role added successfully');
        setShowAddRoleModal(false);
        setNewRole({
          roleName: '',
          description: '',
          skills: '',
          experienceLevel: 'Fresher',
        });

        // Refetch roles for the selected department
        const rolesRes = await jobRoleServices.getRolesByDepartment(
          selectedDepartment
        );
        if (rolesRes.data.success) {
          setRoles(rolesRes.data.roles);
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add role');
    }
  };

  const handleAddNewClient = async (e) => {
    e.preventDefault();
    if (!selectedCompany) return toast.error('Please select a company first');
    try {
      const clientData = { ...newClient, companyId: selectedCompany };
      const response = await clientServices.createClient(clientData);
      if (response.data.success) {
        toast.success('Client added successfully');
        setShowAddClientModal(false);
        setNewClient({
          clientName: '',
          location: '',
          contactPerson: '',
          contactEmail: '',
        });
        await fetchClients(selectedCompany);
        if (response.data.client) setSelectedClient(response.data.client._id);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add client');
    }
  };

  const selectedCount = studentDetails.filter((s) => s.selected).length;

  const getTypedResume = (student, type) =>
    student.resumes?.find((r) => r.resumeType === type) || null;

  const getDisplayResumes = (student) => {
    const fresher = getTypedResume(student, 'Fresher');
    const experience = getTypedResume(student, 'Experience');
    if (fresher || experience) {
      return [
        ...(fresher ? [{ label: 'Fresher', resume: fresher }] : []),
        ...(experience ? [{ label: 'Experience', resume: experience }] : []),
      ];
    }
    return student.resume?.filePath
      ? [{ label: 'Resume', resume: student.resume }]
      : [];
  };

  const selectedResumeCount = studentDetails.filter(
    (s) =>
      s.selected &&
      (s.resume?.filePath ||
        s.resumes?.some((r) => r?.filePath))
  ).length;

  const handleDownloadResume = async (student, resume) => {
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/${resume.filePath}`;
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = resume.fileName || 'resume';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Failed to download resume.');
    }
  };

  const handleDownloadZip = async () => {
    const selectedIds = studentDetails
      .filter(
        (s) =>
          s.selected &&
          (s.resume?.filePath ||
            s.resumes?.some((r) => r?.filePath))
      )
      .map((s) => s._id);
    if (selectedIds.length === 0) {
      setShowDownloadModal(false);
      return toast.error('No resumes found for selected students');
    }
    setDownloadingZip(true);
    try {
      const response = await studentAssignmentServices.downloadResumes(
        selectedIds
      );
      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'resumes.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setShowDownloadModal(false);
      toast.success('Resumes downloaded successfully.');
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to download resumes'
      );
    } finally {
      setDownloadingZip(false);
    }
  };

  return (
    <div className="custom-scrollbar mt-4 h-full w-full">
      {/* Only shown when we arrived from an Ongoing Interviews card, which is
          the one entry point into this page that has somewhere to go back to. */}
      {prefill && (
        <button
          onClick={() =>
            navigate(`/${location.pathname.split('/')[1]}/hr/ongoing-interviews`)
          }
          className="mb-4 flex items-center gap-1 text-sm font-medium text-brand-500 hover:text-brand-600"
        >
          <MdArrowBack className="h-4 w-4" />
          Back to Ongoing Interviews
        </button>
      )}
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
            Assign Students to Company
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Select multiple students and assign them to a company, client, and
            role.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowDownloadModal(true)}
            disabled={selectedResumeCount === 0}
            className={`rounded-xl border-2 border-brand-500 bg-transparent px-6 py-2.5 text-base font-medium text-brand-500 transition duration-200 hover:bg-brand-50 dark:hover:bg-navy-700 ${
              selectedResumeCount === 0
                ? 'cursor-not-allowed opacity-50'
                : ''
            }`}
          >
            Download Resumes
            {selectedResumeCount > 0 ? ` (${selectedResumeCount})` : ''}
          </button>
          <button
            onClick={triggerConfirmation}
            disabled={isSubmitting || selectedCount === 0}
            className={`rounded-xl bg-brand-500 px-8 py-2.5 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 ${
              isSubmitting || selectedCount === 0
                ? 'cursor-not-allowed opacity-70'
                : ''
            }`}
          >
            {isSubmitting
              ? 'Assigning...'
              : `Assign ${
                  selectedCount > 0 ? `(${selectedCount})` : ''
                } Students`}
          </button>
        </div>
      </div>

      {/* --- Step 1: Configuration --- */}
      <Card extra="p-6 mb-6">
        <h3 className="mb-4 border-b pb-2 text-lg font-bold text-navy-700 dark:border-navy-600 dark:text-white">
          Assignment Details
        </h3>

        {/* JD Source: role template vs. an uploaded JD file */}
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center">
          <span className="text-sm font-bold text-navy-700 dark:text-white">
            JD Source
          </span>
          <div className="inline-flex rounded-xl border border-gray-200 p-1 dark:!border-white/10 dark:!bg-navy-800">
            {[
              { key: 'role', label: 'Select Role' },
              { key: 'attachment', label: 'Attach JD' },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => handleJdSourceChange(opt.key)}
                className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
                  jdSource === opt.key
                    ? 'bg-brand-500 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {jdSource === 'role'
              ? 'Pick a saved role template. You can also attach a JD file.'
              : 'Upload the JD document. Role and experience are not required.'}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Company Selection */}
          <div>
            <label className="mb-2 block text-sm font-bold text-navy-700 dark:text-white">
              Company *
            </label>
            <select
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
              value={selectedCompany}
              onChange={handleCompanyChange}
            >
              <option value="">Select Company</option>
              {companies.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name} {c.hasClients ? '(Agency)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Client Selection (Conditional) */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="block text-sm font-bold text-navy-700 dark:text-white">
                {selectedCompanyObj?.hasClients
                  ? 'Client *'
                  : 'Client (Not Applicable)'}
              </label>
              {selectedCompanyObj?.hasClients && (
                <button
                  onClick={() => setShowAddClientModal(true)}
                  className="text-xs font-medium text-brand-500 hover:underline"
                >
                  + Add New
                </button>
              )}
            </div>
            <select
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-normal outline-none disabled:bg-gray-100 disabled:text-gray-400 dark:!border-white/10 dark:!bg-navy-800 dark:text-white dark:disabled:bg-navy-700"
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              disabled={!selectedCompanyObj?.hasClients}
            >
              <option value="">
                {selectedCompanyObj?.hasClients
                  ? 'Select Client'
                  : 'Direct to Company'}
              </option>
              {selectedCompanyObj?.hasClients &&
                clients.map((cl) => (
                  <option key={cl._id} value={cl._id}>
                    {cl.clientName}
                  </option>
                ))}
            </select>
          </div>

          {/* Department Selection */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="block text-sm font-bold text-navy-700 dark:text-white">
                Department *
              </label>
            </div>
            <select
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
              value={selectedDepartment}
              onChange={handleDepartmentChange}
            >
              <option value="">Select Department</option>
              {departments.map((d) => (
                <option key={d._id} value={d._id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {jdSource === 'role' && (
            <>
              {/* Role Name Selection */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="block text-sm font-bold text-navy-700 dark:text-white">
                    Role *
                  </label>
                  {selectedDepartment && (
                    <button
                      onClick={() => setShowAddRoleModal(true)}
                      className="text-xs font-medium text-brand-500 hover:underline"
                    >
                      + Add New
                    </button>
                  )}
                </div>
                <select
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-normal outline-none disabled:bg-gray-100 disabled:text-gray-400 dark:!border-white/10 dark:!bg-navy-800 dark:text-white dark:disabled:bg-navy-700"
                  value={selectedRoleName}
                  onChange={handleRoleNameChange}
                  disabled={!selectedDepartment}
                >
                  <option value="">Select Role</option>
                  {uniqueRoleNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Experience Level Selection (Dependent on Role Name) */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="block text-sm font-bold text-navy-700 dark:text-white">
                    Experience Level *
                  </label>
                </div>
                <select
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-normal outline-none disabled:bg-gray-100 disabled:text-gray-400 dark:!border-white/10 dark:!bg-navy-800 dark:text-white dark:disabled:bg-navy-700"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  disabled={!selectedRoleName}
                >
                  <option value="">Select Experience</option>
                  {availableExperienceLevels.map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.experienceLevel}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {jdSource === 'attachment' && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="block text-sm font-bold text-navy-700 dark:text-white">
                  Role *
                </label>
              </div>
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="e.g. Backend Developer Intern"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-400">
                Lets you assign the same student to this company again for a different role/JD.
              </p>
            </div>
          )}

          {/* JD Attachment */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="block text-sm font-bold text-navy-700 dark:text-white">
                {jdSource === 'attachment' ? 'Attach JD *' : 'Attach JD (optional)'}
              </label>
            </div>
            <input
              ref={jdFileInputRef}
              type="file"
              accept={JD_ACCEPT}
              onChange={handleJdFileChange}
              className="hidden"
            />
            {jdFile ? (
              <div className="flex w-full items-center gap-2 rounded-xl border border-gray-200 px-3 py-2.5 text-sm dark:!border-white/10 dark:!bg-navy-800">
                <span className="truncate text-navy-700 dark:text-white" title={jdFile.name}>
                  {jdFile.name}
                </span>
                <span className="shrink-0 text-xs text-gray-500">
                  {formatFileSize(jdFile.size)}
                </span>
                <button
                  type="button"
                  onClick={clearJdFile}
                  className="ml-auto shrink-0 text-gray-400 hover:text-red-500"
                  title="Remove file"
                >
                  &#10005;
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => jdFileInputRef.current?.click()}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-left text-sm text-gray-500 hover:bg-gray-50 dark:!border-white/10 dark:!bg-navy-800 dark:hover:bg-navy-700"
              >
                Choose file
              </button>
            )}
            <p className="mt-1 text-xs text-gray-400">
              PDF, DOC, DOCX, TXT, RTF, ODT, PNG, JPG &middot; max 10 MB
            </p>
          </div>

          {/* Assignment Expiry */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="block text-sm font-bold text-navy-700 dark:text-white">
                Assignment Expiry
              </label>
            </div>
            <select
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
              value={assignmentExpiry}
              onChange={(e) => setAssignmentExpiry(Number(e.target.value))}
            >
              <option value={24}>24 Hours (Default)</option>
              <option value={12}>12 Hours</option>
              <option value={6}>6 Hours</option>
            </select>
          </div>
        </div>
      </Card>

      {/* --- Step 2: Student Data & Filters --- */}
      <Card extra="p-6">
        <h3 className="mb-4 border-b pb-2 text-lg font-bold text-navy-700 dark:border-navy-600 dark:text-white">
          Select Students
        </h3>

        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <input
            type="text"
            placeholder="Search name, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-lightPrimary px-3 py-2 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          />
          <select
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none dark:!bg-navy-800 dark:text-white"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
          <select
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none dark:!bg-navy-800 dark:text-white"
            value={jobPriorityFilter}
            onChange={(e) => setJobPriorityFilter(e.target.value)}
          >
            <option value="all">Priority</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none dark:!bg-navy-800 dark:text-white"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
          >
            <option value="all">Location Pref</option>
            {uniqueLocations.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <select
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none dark:!bg-navy-800 dark:text-white"
            value={salaryFilter}
            onChange={(e) => setSalaryFilter(e.target.value)}
          >
            <option value="all">Salary Pref</option>
            <option value="Any Salary">Any Salary</option>
            <option value="Below 3 LPA">Below 3 LPA</option>
            <option value="Below 4 LPA">Below 4 LPA</option>
            <option value="Below 5 LPA">Below 5 LPA</option>
            <option value="More than 5 LPA">More than 5 LPA</option>
          </select>
          <select
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none dark:!bg-navy-800 dark:text-white"
            value={courseFilter}
            onChange={(e) => setCourseFilter(e.target.value)}
          >
            <option value="all">Course</option>
            {uniqueCourses.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <button
            onClick={resetFilters}
            className="w-full rounded-xl bg-gray-100 py-2.5 text-sm font-medium text-navy-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
          >
            Reset Filters
          </button>
        </div>

        {/* Data Table */}
        {loadingStudents ? (
          <div className="flex h-40 w-full items-center justify-center">
            <ThreeDots color="#5431ed" />
          </div>
        ) : filteredData.length === 0 ? (
          <Nodatafound title="No Student data found" />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-navy-600">
            <table className="w-full text-left text-sm text-gray-600 dark:text-gray-400">
              <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-navy-700 dark:text-gray-300">
                <tr>
                  <th className="px-6 py-3">
                    <Checkbox
                      onChange={handleSelectAll}
                      checked={
                        filteredData.length > 0 &&
                        filteredData.every((s) => s.selected)
                      }
                    />
                  </th>
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Course</th>
                  <th className="px-6 py-3">Email / Phone</th>
                  <th className="px-6 py-3">Priority</th>
                  <th className="px-6 py-3">Location Pref</th>
                  <th className="px-6 py-3">Salary Pref</th>
                  <th className="px-6 py-3">Education</th>
                  <th className="px-6 py-3">Address</th>
                  <th className="px-6 py-3">Resume</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((student) => (
                  <tr
                    key={student._id}
                    className="border-b bg-white hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-800 dark:hover:bg-navy-700"
                  >
                    <td className="px-6 py-4">
                      <Checkbox
                        onChange={() => handleCheckboxChange(student._id)}
                        checked={student.selected || false}
                      />
                    </td>
                    <td className="max-w-[150px] truncate px-6 py-4 font-medium text-navy-700 dark:text-white">
                      {student.name}
                    </td>
                    <td className="max-w-[150px] truncate px-6 py-4 text-xs">
                      {student.enrollments
                        ?.filter((e) => e.current)
                        ?.map((e) => (
                          <div key={e._id}>{e.course?.courseName || '-'}</div>
                        ))}
                      {(!student.enrollments ||
                        student.enrollments.filter((e) => e.current).length ===
                          0) &&
                        '-'}
                    </td>
                    <td className="max-w-[200px] truncate px-6 py-4 text-xs">
                      <div>{student.email}</div>
                      <div className="text-gray-500">
                        {student.contactNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`rounded-full px-2 py-1 text-[10px] font-bold ${
                          student.placementPriority === 'High'
                            ? 'bg-green-100 text-green-700'
                            : student.placementPriority === 'Medium'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {student.placementPriority}
                      </span>
                    </td>
                    <td
                      className="max-w-[150px] truncate px-6 py-4"
                      title={student.jobLocationPreference.join(', ')}
                    >
                      {student.jobLocationPreference.join(', ') || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-semibold ${
                          student.preferredSalary === 'More than 5 LPA'
                            ? 'bg-green-100 text-green-700'
                            : student.preferredSalary === 'Below 5 LPA'
                            ? 'bg-blue-100 text-blue-700'
                            : student.preferredSalary === 'Below 4 LPA'
                            ? 'bg-yellow-100 text-yellow-700'
                            : student.preferredSalary === 'Below 3 LPA'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {student.preferredSalary || 'Any Salary'}
                      </span>
                    </td>
                    <td className="max-w-[200px] px-6 py-4 text-xs">
                      {student.academicRecords?.map((r) => (
                        <div key={r._id}>
                          {r.level} {r.percentage ? `(${r.percentage}%)` : ''}
                        </div>
                      ))}
                    </td>
                    <td
                      className="max-w-[200px] truncate px-6 py-4 text-xs"
                      title={
                        student.address
                          ? `${student.address.street || ''}, ${
                              student.address.city || ''
                            }, ${student.address.state || ''}`
                          : ''
                      }
                    >
                      {student.address ? (
                        <>
                          {student.address.city && (
                            <span>{student.address.city}</span>
                          )}
                          {student.address.state && (
                            <span>, {student.address.state}</span>
                          )}
                        </>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="max-w-[180px] px-6 py-4 text-xs">
                      {getDisplayResumes(student).length > 0 ? (
                        <div className="flex flex-col gap-2">
                          {getDisplayResumes(student).map(({ label, resume }) => (
                            <div key={label} className="flex flex-col gap-1">
                              <span
                                className="max-w-[160px] truncate font-semibold text-gray-700 dark:text-gray-300"
                                title={resume.fileName}
                              >
                                {label}: 📄{' '}
                                <span className="font-normal">{resume.fileName}</span>
                              </span>
                              <div className="flex gap-2">
                                <a
                                  href={`${process.env.REACT_APP_API_BASE_URL}/${resume.filePath}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="rounded bg-brand-500 px-2 py-1 text-xs font-medium text-white hover:bg-brand-600"
                                >
                                  View
                                </a>
                                <button
                                  onClick={() => handleDownloadResume(student, resume)}
                                  className="rounded bg-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                                >
                                  Download
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">
                          No Resume
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* --- Modals --- */}
      <ConfirmationModal
        isOpen={isModalOpenConfirmation}
        onClose={() => {
          if (!isSubmitting) setIsModalOpenConfirmation(false);
        }}
        onConfirm={handleSubmit}
        title="Confirm Assignment"
        message={`Are you sure you want to assign ${selectedCount} students to ${selectedCompanyObj?.name}? ${
          jdSource === 'attachment'
            ? `Role: ${jobTitle || '-'} (JD: ${jdFile?.name || '-'})`
            : `Role: ${selectedRoleName || '-'}${jdFile ? ` (JD: ${jdFile.name})` : ''}`
        }. Expiry: ${assignmentExpiry} hours.`}
        confirmText={isSubmitting ? 'Assigning...' : 'Confirm'}
        cancelText="Cancel"
        icon="info"
        disableConfirm={isSubmitting}
      />

      {showDownloadModal && (
        <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-navy-800 md:mx-0">
            <h3 className="mb-2 text-xl font-bold text-navy-700 dark:text-white">
              Download Resumes
            </h3>
            <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
              You have selected {selectedResumeCount} students with resumes.
              Download all their resumes as a ZIP file.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDownloadModal(false)}
                disabled={downloadingZip}
                className={`rounded-xl bg-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-400 ${
                  downloadingZip ? 'cursor-not-allowed opacity-70' : ''
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleDownloadZip}
                disabled={downloadingZip}
                className={`flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2 font-medium text-white hover:bg-brand-600 ${
                  downloadingZip ? 'cursor-not-allowed opacity-70' : ''
                }`}
              >
                {downloadingZip && <ThreeDots height="16" width="16" color="#fff" />}
                {downloadingZip ? 'Downloading...' : 'Download ZIP'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddRoleModal && (
        <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
          <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-xl bg-white p-6 dark:bg-navy-800">
            <h3 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
              Add New Role to Department
            </h3>
            <form onSubmit={handleAddNewRole} className="space-y-4">
              <InputField
                label="Role Name *"
                value={newRole.roleName}
                onChange={(e) =>
                  setNewRole({ ...newRole, roleName: e.target.value })
                }
                placeholder="e.g., React Developer"
                required
              />
              <InputField
                label="Description"
                value={newRole.description}
                onChange={(e) =>
                  setNewRole({ ...newRole, description: e.target.value })
                }
                placeholder="Role description"
              />
              <InputField
                label="Skills (comma separated)"
                value={newRole.skills}
                onChange={(e) =>
                  setNewRole({ ...newRole, skills: e.target.value })
                }
                placeholder="e.g., React, Node.js"
              />
              <div>
                <label className="mb-2 block text-sm font-bold text-navy-700 dark:text-white">
                  Experience Level
                </label>
                <select
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                  value={newRole.experienceLevel}
                  onChange={(e) =>
                    setNewRole({ ...newRole, experienceLevel: e.target.value })
                  }
                >
                  <option value="Fresher">Fresher</option>
                  <option value="Junior">Junior</option>
                  <option value="Mid-level">Mid-level</option>
                  <option value="Senior">Senior</option>
                  <option value="Lead">Lead</option>
                  <option value="Expert">Expert</option>
                  <option value="Intern">Intern</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddRoleModal(false)}
                  className="flex-1 rounded-xl bg-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 rounded-xl bg-brand-500 px-4 py-2 font-medium text-white hover:bg-brand-600"
                >
                  Add Role
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddClientModal && (
        <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
          <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-xl bg-white p-6 dark:bg-navy-800">
            <h3 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
              Add New Client
            </h3>
            <form onSubmit={handleAddNewClient} className="space-y-4">
              <InputField
                label="Client Name *"
                value={newClient.clientName}
                onChange={(e) =>
                  setNewClient({ ...newClient, clientName: e.target.value })
                }
                placeholder="e.g., TCS Mumbai"
                required
              />
              <InputField
                label="Location"
                value={newClient.location}
                onChange={(e) =>
                  setNewClient({ ...newClient, location: e.target.value })
                }
                placeholder="e.g., Mumbai"
              />
              <InputField
                label="Contact Person"
                value={newClient.contactPerson}
                onChange={(e) =>
                  setNewClient({ ...newClient, contactPerson: e.target.value })
                }
                placeholder="e.g., John Doe"
              />
              <InputField
                label="Contact Email"
                type="email"
                value={newClient.contactEmail}
                onChange={(e) =>
                  setNewClient({ ...newClient, contactEmail: e.target.value })
                }
                placeholder="contact@client.com"
              />
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddClientModal(false)}
                  className="flex-1 rounded-xl bg-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 rounded-xl bg-brand-500 px-4 py-2 font-medium text-white hover:bg-brand-600"
                >
                  Add Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;
